import { useCallback, useEffect, useMemo, useState } from "react";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Customer } from "@/graphql/__generated__/graphql";
import { useFetchCustomersLazyQueryHook } from "@/hooks/queries";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import useUserStore from "@/store/shield";
import Fuse from "fuse.js";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";

interface Props {
  form: UseFormReturn<FieldValues>;
  formPrefix: string;  // e.g. 'quick.description' or 'description'
  prefilled?: boolean;
  aiImportID?: string | null;
  className?: string;
};

/**
 * CustomerNameComponent is a component that handles the customer name input field
 * It also handles the search and selection of existing customers
 */
export function CustomerNameComponent({ form, formPrefix, prefilled = false, aiImportID, className }: Props) {

  // Hooks
  const { t } = useTranslation();
  const { fetchCustomers, customers } = useFetchCustomersLazyQueryHook();

  // States
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [didYouMean, setDidYouMean] = useState(false);
  const [search, setSearch] = useState("");

  // Get the logged in user id
  const userId = useUserStore((state) => state.userId);

  // Init Fuse instance
  const fuse = useMemo(() => new Fuse(customers || [], {
    keys: ["name"],
    threshold: 0.3, // Lower threshold = more strict matching (0.0 - 1.0)
    minMatchCharLength: 1,
    includeScore: true,
    shouldSort: true,
  }), [customers]);

  /**
   * AIPrefillSelection is a function that will prefill the customer name and id
   * based on the customer id that is passed from the AI import
   */
  const AIPrefillSelection = () => {
    const formValues = form.getValues();
    const customerId = formValues[formPrefix]?.customerId;
    const customerName = formValues[formPrefix]?.customerName;

    if (!customers) {
      return;
    }

    if (customerId && customers.length > 0) {
      // find the customer from the list of customers
      const matchedCustomer = customers.find((customer) => customer.id === customerId);

      // if there is a matched customer, it will then assign the customer name to the form
      if (matchedCustomer) {
        handleSelectCustomer(matchedCustomer.name, matchedCustomer.id);
      } else {
        // if there is no matched customer, it will assume that the customer id is not valid
        // and it will create a new customer
        form.setValue(`${formPrefix}.customerId`, undefined);
        if (customerName) {
          form.setValue(`${formPrefix}.customerName`, customerName);
        }
      }
    } else if (customerName) {
      // if there is no customer id but we have a name, set the form value
      form.setValue(`${formPrefix}.customerName`, customerName);
    }
  };

  const handleSelectCustomer = (name: string, id: number) => {
    form.setValue(`${formPrefix}.customerName`, name);
    form.setValue(`${formPrefix}.customerId`, id);
    setPopoverVisible(false);
  };

  // Set the owner ID when component mounts
  useEffect(() => {
    if (userId) {
      form.setValue(`${formPrefix}.ownerId`, userId);
    }
  }, [userId, formPrefix, form]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const customerName = form.getValues(`${formPrefix}.customerName`);
    if (!customerName) {
      setFilteredCustomers(customers || []);
      return;
    }

    // Fuse fuzzy search
    const results = fuse.search(customerName);

    setFilteredCustomers(results.map(result => result.item));

    if (customerName && results.length === 1 && results[0].item.name !== customerName) {
      setDidYouMean(true);
    } else {
      setDidYouMean(false);
    }
  }, [form.watch(`${formPrefix}.customerName`), customers]);

  useEffect(() => {
    if (aiImportID && prefilled && customers) {
      AIPrefillSelection();
    }
  }, [prefilled, customers]);

  // Watch for form value changes and update search state
  const watchedCustomerName = form.watch(`${formPrefix}.customerName`);
  useEffect(() => {
    // Only update search if the watched value is different from current search
    // This prevents interference with user input while still syncing form changes
    if (watchedCustomerName !== search) {
      setSearch(watchedCustomerName ?? "");
    }
  }, [watchedCustomerName, search]);

  const debouncedFuseSearch = useCallback(
    debounce((value: string) => {
      if (value && customers?.length) {
        const results = fuse.search(value);
        if (results.length > 1 && results[0].item.name.toLowerCase() === value.toLowerCase()) {
          const matchedCustomer = results[0].item;
          form.setValue(`${formPrefix}.customerId`, matchedCustomer.id);
        } else {
          form.setValue(`${formPrefix}.customerId`, undefined);
        }
      }
    }, 300),
    [customers, fuse, formPrefix, form]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    form.setValue(`${formPrefix}.customerName`, value);
    debouncedFuseSearch(value);
  };

  /**
   * Handles the blur event for the customer name input
   * If focus moves to the popover dialog, does nothing
   * If there is input and a customerId, finds the matching customer and sets their name
   * Always closes the popover when blurring (unless moving to popover)
   * @param {React.FocusEvent} e - The blur event object
   */
  const handleBlur = (e: React.FocusEvent) => {
    // If the next focused element is within the popover, don't process the onBlur
    if (e.relatedTarget && e.relatedTarget.closest("[role=\"dialog\"]")) {
      return;
    }

    // If there's input and a customerId, find that customer and set their name
    const customerName = form.getValues(`${formPrefix}.customerName`);
    if (customerName && form.getValues(`${formPrefix}.customerId`)) {
      const customerId = form.getValues(`${formPrefix}.customerId`);
      const customer = customers?.find(
        (customer) => customer.id === customerId
      );
      if (customer) {
        form.setValue(`${formPrefix}.customerName`, customer.name);
      }
    }
    setPopoverVisible(false);
  };

  return (
    <FormField
      control={form.control}
      name={`${formPrefix}.customerName`}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col items-start w-full", className)}>
          <FormLabel>{t("newtender.formgroups.description.inputs.customername.label")}</FormLabel>
          <FormControl>
            <Input
              placeholder={t("newtender.formgroups.description.inputs.customername.placeholder")}
              {...field}
              onClick={() => {
                setPopoverVisible(true);
                setDidYouMean(false);
              }}
              onChange={handleInputChange}
              onBlur={handleBlur}
              autoComplete="off"
              className="text-sm"
            />
          </FormControl>
          <FormDescription>{t("newtender.formgroups.description.inputs.customername.description")}</FormDescription>
          <FormMessage />
          <Popover open={popoverVisible} onOpenChange={setPopoverVisible}>
            <PopoverTrigger />
            <PopoverContent className="fixed top-[-30px] w-90 max-h-60 overflow-y-auto p-1" onOpenAutoFocus={(e) => e.preventDefault()}>
              {
                didYouMean && (
                  <div className="py-2 px-4 text-sm text-gray-500">
                    {t("newtender.formgroups.description.inputs.customername.didyoumean")}
                  </div>
                )
              }
              <ul>
                {filteredCustomers.map((customer: Customer) => {
                  return (
                    <li
                      key={customer.id}
                      className="text-sm cursor-pointer rounded-xs hover:bg-gray-100 py-2 px-4 flex justify-between items-center"
                      onClick={() => handleSelectCustomer(customer.name, customer.id)}
                    >
                      <span>{customer.name}</span>
                    </li>
                  );
                })}
                {filteredCustomers.length === 0 && (
                  <li className="p-2 text-sm text-gray-500">
                    {t("newtender.formgroups.description.inputs.customername.nocustomerfound")}
                  </li>
                )}
              </ul>
            </PopoverContent>
          </Popover>
        </FormItem>
      )}
    />
  );
}
